import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, HelpCircle, Loader2, X, RotateCcw } from "lucide-react";
import {
  newGame,
  exposed,
  current,
  playableIds,
  play,
  draw,
  addDraws,
  isStuck,
  levelConfig,
  goalReward,
  isGoalLevel,
  flowerForLevel,
  sanitizeProgress,
  TOTAL_LEVELS,
  GOAL_EVERY,
  powerTypes,
  POWER_META,
  SUIT_FLOWER,
  SUIT_COLOR,
  BOARD_UNITS_W,
} from "@/lib/nectartrail";

/* ── board geometry ─────────────────────────────────────────────────────────── */
const UNIT = 27;
const CW = UNIT * 2;
const CH = 76;
const ROW_H = 34;
const BOARD_W = BOARD_UNITS_W * UNIT;
const BOARD_H = 3 * ROW_H + CH;
const rankLabel = (r) => (r === 1 ? "1" : String(r));

const HIVE_TIERS = [0, 200, 600, 1500, 3000, 6000, 12000];
const hiveTier = (honey) => HIVE_TIERS.filter((t) => honey >= t).length - 1;

/* ── one board card ─────────────────────────────────────────────────────────── */
function BoardCard({ card, isExposed, playable, onTap, shaking }) {
  const left = card.x * UNIT;
  const top = card.row * ROW_H;
  if (!isExposed) {
    return (
      <g transform={`translate(${left},${top})`}>
        <rect width={CW} height={CH} rx="8" fill="#F1C25B" stroke="#C6871F" strokeWidth="1.5" />
        <rect x="6" y="6" width={CW - 12} height={CH - 12} rx="5" fill="none" stroke="#E7A93A" strokeWidth="1.5" strokeDasharray="3 3" />
        <text x={CW / 2} y={CH / 2 + 6} textAnchor="middle" fontSize="18" opacity="0.65">🍯</text>
      </g>
    );
  }
  const flower = SUIT_FLOWER[card.suit];
  const color = SUIT_COLOR[card.suit];
  return (
    <g transform={`translate(${left},${top})`} style={{ cursor: "pointer" }} onClick={() => onTap(card)} className={shaking ? "nt-shake" : ""} data-testid={`nt-card-${card.id}`}>
      <rect width={CW} height={CH} rx="8" fill="#fff" stroke={playable ? "#22C55E" : "#E4DDCE"} strokeWidth={playable ? 3 : 1.5} />
      {playable && <rect width={CW} height={CH} rx="8" fill="none" stroke="#86EFAC" strokeWidth="6" opacity="0.4" />}
      <text x="9" y="21" fontSize="17" fontWeight="700" fill={color}>{rankLabel(card.rank)}</text>
      <text x={CW - 9} y={CH - 10} textAnchor="end" fontSize="15">{flower}</text>
      <text x={CW / 2} y={CH / 2 + 9} textAnchor="middle" fontSize="24">{flower}</text>
    </g>
  );
}

/* ── small card used for stock / waste ──────────────────────────────────────── */
function MiniCard({ card, back, bee, count, dim }) {
  return (
    <div className={`relative grid place-items-center rounded-xl border-2 ${back ? "border-amber-500 bg-amber-300" : "border-stone-200 bg-white"} ${dim ? "opacity-40" : ""}`} style={{ width: 62, height: 84 }}>
      {back ? (
        <span className="text-2xl opacity-70">🍯</span>
      ) : card ? (
        <>
          <span className="absolute left-1.5 top-1 text-lg font-bold" style={{ color: SUIT_COLOR[card.suit] }}>{rankLabel(card.rank)}</span>
          <span className="text-3xl">{SUIT_FLOWER[card.suit]}</span>
        </>
      ) : (
        <span className="text-xs text-stone-300">empty</span>
      )}
      {bee && <span className="absolute -top-3 -right-2 text-2xl drop-shadow">🐝</span>}
      {count != null && <span className="absolute -bottom-2 left-1/2 -translate-x-1/2 rounded-full bg-stone-800 px-1.5 text-[10px] font-semibold text-white">{count}</span>}
    </div>
  );
}

export default function NectarTrail() {
  const [prog, setProg] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [view, setView] = useState("map");
  const [showHelp, setShowHelp] = useState(false);

  const [game, setGame] = useState(null);
  const [attempt, setAttempt] = useState(0);
  const [timeLeft, setTimeLeft] = useState(0);
  const [result, setResult] = useState(null); // { type, reason, drops, reward }
  const [wildActive, setWildActive] = useState(false);
  const [history, setHistory] = useState([]);
  const [shakeId, setShakeId] = useState(null);
  const mapScroll = useRef(null);

  // load / save progress
  useEffect(() => {
    let cancelled = false;
    api
      .get("/games/nectartrail")
      .then((r) => !cancelled && setProg(sanitizeProgress(r.data)))
      .catch(() => setProg(sanitizeProgress(null)))
      .finally(() => !cancelled && setLoaded(true));
    return () => {
      cancelled = true;
    };
  }, []);

  const saveTimer = useRef(null);
  useEffect(() => {
    if (!loaded || !prog) return undefined;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => api.put("/games/nectartrail", prog).catch(() => {}), 500);
    return () => saveTimer.current && clearTimeout(saveTimer.current);
  }, [loaded, prog]);

  // countdown
  useEffect(() => {
    if (view !== "game" || result) return undefined;
    const id = setInterval(() => setTimeLeft((v) => Math.max(0, v - 1)), 1000);
    return () => clearInterval(id);
  }, [view, result]);

  const doLose = useCallback((reason) => {
    setResult((r) => r || { type: "lost", reason });
  }, []);

  useEffect(() => {
    if (view === "game" && !result && timeLeft === 0 && game) doLose("time");
  }, [timeLeft, view, result, game, doLose]);

  // auto-scroll the map to the current flower
  useEffect(() => {
    if (view !== "map" || !prog || !mapScroll.current) return;
    const el = mapScroll.current;
    const y = 40 + (prog.level - 1) * 66;
    el.scrollTop = Math.max(0, y - el.clientHeight / 2);
  }, [view, prog]);

  const startLevel = useCallback((level, att = 0) => {
    setGame(newGame(level, att));
    setAttempt(att);
    setTimeLeft(levelConfig(level).time);
    setHistory([]);
    setWildActive(false);
    setResult(null);
    setView("game");
  }, []);

  const canRescue = prog && (prog.pu.draw > 0 || prog.pu.wild > 0);

  const pushHistory = useCallback((g) => setHistory((h) => [...h.slice(-30), g]), []);

  const backToMap = useCallback(() => {
    setResult(null);
    setView("map");
  }, []);

  const finishWin = useCallback(() => {
    const att = game.attempt || 0;
    const bonus = Math.max(0, 50 - att * 10); // 50 first try, 10 fewer per retry
    const secs = timeLeft;
    const drops = secs + bonus; // seconds left + attempt bonus
    let reward = null;
    setProg((p) => {
      const np = { ...p, honey: p.honey + drops, pu: { ...p.pu } };
      if (game.level === p.level) {
        np.level = Math.min(TOTAL_LEVELS + 1, p.level + 1);
        if (isGoalLevel(game.level)) {
          reward = goalReward(game.level);
          np.pu[reward] = (np.pu[reward] || 0) + 1;
        }
      }
      return np;
    });
    setResult({ type: "won", drops, secs, bonus, reward, first: att === 0 });
  }, [game, timeLeft]);

  const onTapCard = useCallback(
    (card) => {
      if (result || !game) return;
      if (!exposed(game.cards, card.id)) return;
      const res = play(game, card.id, wildActive);
      if (!res.ok) {
        setShakeId(card.id);
        setTimeout(() => setShakeId((v) => (v === card.id ? null : v)), 360);
        return;
      }
      pushHistory(game);
      setWildActive(false);
      setGame(res.state);
      if (res.state.won) finishWin();
      else if (isStuck(res.state) && !canRescue) doLose("stuck");
    },
    [game, result, wildActive, canRescue, pushHistory, finishWin, doLose]
  );

  const onDraw = useCallback(() => {
    if (result || !game) return;
    const res = draw(game);
    if (!res.ok) return;
    pushHistory(game);
    setGame(res.state);
    if (isStuck(res.state) && !canRescue) doLose("stuck");
  }, [game, result, canRescue, pushHistory, doLose]);

  const applyPower = useCallback(
    (type) => {
      if (!prog || result || prog.pu[type] <= 0) return;
      if (type === "undo" && history.length === 0) {
        toast.error("Nothing to undo yet");
        return;
      }
      setProg((p) => ({ ...p, pu: { ...p.pu, [type]: p.pu[type] - 1 } }));
      if (type === "time") {
        setTimeLeft((t) => t + 20);
        toast.success("+20 seconds");
      } else if (type === "draw") {
        setGame((g) => addDraws(g, 3));
        toast.success("+3 nectar cards");
      } else if (type === "wild") {
        setWildActive(true);
        toast.success("Wild bloom ready — tap any flower");
      } else if (type === "undo") {
        setGame(history[history.length - 1]);
        setHistory((h) => h.slice(0, -1));
      }
    },
    [prog, result, history]
  );

  const playable = useMemo(() => (game ? new Set(playableIds(game, wildActive)) : new Set()), [game, wildActive]);

  if (!loaded || !prog) {
    return (
      <div className="grid min-h-[60vh] place-items-center text-stone-400">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  const tier = hiveTier(prog.honey);
  const nextTier = HIVE_TIERS[tier + 1];
  const done = prog.level > TOTAL_LEVELS;

  return (
    <div className="mx-auto max-w-3xl space-y-4" data-testid="nectartrail-page">
      <style>{`
        @keyframes nt-shake { 0%,100%{transform:translate(0,0)} 25%{transform:translate(-3px,0)} 75%{transform:translate(3px,0)} }
        .nt-shake { animation: nt-shake .34s ease-in-out; }
        @keyframes nt-bee { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-4px)} }
        .nt-bee { animation: nt-bee 1.1s ease-in-out infinite; }
      `}</style>

      {/* header */}
      <div className="flex items-center justify-between">
        <Link to="/app/games" className="inline-flex items-center gap-1.5 text-sm text-stone-500 hover:text-stone-800">
          <ArrowLeft className="h-4 w-4" /> Games
        </Link>
        <div className="flex items-center gap-2">
          <span className="rounded-full bg-amber-100 px-3 py-1 text-sm font-semibold text-amber-700" data-testid="nt-honey">🍯 {prog.honey.toLocaleString()}</span>
          <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => setShowHelp(true)}><HelpCircle className="h-4 w-4" /></Button>
        </div>
      </div>

      <div>
        <h1 className="font-display text-3xl font-semibold tracking-tight text-stone-800">Nectar Trail 🐝</h1>
        <p className="mt-1 text-stone-500">Guide the bee flower to flower, clearing each bloom's peaks before the timer runs out. Carry honey home to the hive.</p>
      </div>

      {view === "map" ? (
        <MapView prog={prog} tier={tier} nextTier={nextTier} done={done} scrollRef={mapScroll} onPlay={() => startLevel(prog.level)} />
      ) : (
        <GameView
          game={game}
          timeLeft={timeLeft}
          playable={playable}
          shakeId={shakeId}
          wildActive={wildActive}
          prog={prog}
          onTapCard={onTapCard}
          onDraw={onDraw}
          applyPower={applyPower}
          onBack={backToMap}
        />
      )}

      {/* game-over overlays */}
      {result && game && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4">
          <div className="w-full max-w-sm rounded-2xl bg-white p-6 text-center shadow-2xl">
            {result.type === "won" ? (
              <>
                <div className="text-5xl">🐝🍯</div>
                <h2 className="mt-3 font-display text-2xl font-semibold text-stone-800">Flower cleared!</h2>
                <p className="mt-1 text-stone-500">+{result.drops} honey drops — {result.secs}s left{result.bonus > 0 ? ` + ${result.bonus} ${result.first ? "first-try" : "retry"} bonus` : ""}</p>
                {result.reward && (
                  <p className="mt-2 rounded-lg bg-amber-50 px-3 py-2 text-sm font-medium text-amber-700">Goal bloom! Earned {POWER_META[result.reward].icon} {POWER_META[result.reward].label}</p>
                )}
                <Button className="mt-5 w-full" onClick={backToMap} data-testid="nt-continue">Continue the trail →</Button>
              </>
            ) : (
              <>
                <div className="text-5xl">{result.reason === "time" ? "⏱️" : "🥀"}</div>
                <h2 className="mt-3 font-display text-2xl font-semibold text-stone-800">{result.reason === "time" ? "Out of time!" : "No moves left"}</h2>
                <p className="mt-1 text-stone-500">The bee needs to try this flower again — a fresh deal awaits.</p>
                <div className="mt-5 flex gap-2">
                  <Button variant="outline" className="flex-1" onClick={backToMap}>To map</Button>
                  <Button className="flex-1" onClick={() => startLevel(game.level, attempt + 1)} data-testid="nt-retry"><RotateCcw className="mr-1 h-4 w-4" /> Retry</Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {showHelp && <HelpModal onClose={() => setShowHelp(false)} />}
    </div>
  );
}

/* ── map view ───────────────────────────────────────────────────────────────── */
function MapView({ prog, tier, nextTier, done, scrollRef, onPlay }) {
  const SP = 66;
  const AMP = 92;
  const CX = 160;
  const W = 320;
  const dotX = (i) => CX + AMP * Math.sin(i * 0.55);
  const dotY = (i) => 40 + (i - 1) * SP;
  const totalH = dotY(TOTAL_LEVELS) + 145;
  const pathD = Array.from({ length: TOTAL_LEVELS }, (_, k) => `${k === 0 ? "M" : "L"} ${dotX(k + 1).toFixed(0)} ${dotY(k + 1).toFixed(0)}`).join(" ") + ` L ${CX} ${(dotY(TOTAL_LEVELS) + 90).toFixed(0)}`;

  return (
    <div className="space-y-3">
      {/* hive HUD */}
      <div className="flex items-center gap-3 rounded-2xl border border-stone-200 bg-white p-4">
        <div className="text-4xl" style={{ fontSize: `${28 + tier * 5}px` }}>🐝</div>
        <div className="flex-1">
          <div className="font-display font-semibold text-stone-800">Hive — tier {tier}</div>
          <div className="text-xs text-stone-500">
            {done ? "You reached the hive! Keep collecting honey." : `Flower ${prog.level} of ${TOTAL_LEVELS}`}
            {nextTier ? ` · ${(nextTier - prog.honey).toLocaleString()} honey to next tier` : " · max tier!"}
          </div>
        </div>
        <div className="text-right">
          <div className="text-lg font-bold text-amber-600">🍯 {prog.honey.toLocaleString()}</div>
        </div>
      </div>

      {/* power-up inventory */}
      <div className="flex flex-wrap gap-2">
        {powerTypes.map((t) => (
          <span key={t} className="inline-flex items-center gap-1 rounded-full border border-stone-200 bg-white px-2.5 py-1 text-xs text-stone-600" title={POWER_META[t].desc}>
            {POWER_META[t].icon} {POWER_META[t].label} <b className="text-stone-800">×{prog.pu[t]}</b>
          </span>
        ))}
      </div>

      {/* the trail */}
      <div ref={scrollRef} className="max-h-[58vh] overflow-y-auto rounded-2xl border border-stone-200 bg-gradient-to-b from-sky-50 to-emerald-50 p-2" data-testid="nt-map">
        <svg viewBox={`0 0 ${W} ${totalH}`} width="100%" style={{ display: "block", maxWidth: 360, margin: "0 auto" }}>
          <path d={pathD} fill="none" stroke="#A8A29E" strokeWidth="3" strokeDasharray="2 9" strokeLinecap="round" />
          {/* beehive at the end of the trail */}
          <g transform={`translate(${CX}, ${dotY(TOTAL_LEVELS) + 82})`}>
            <ellipse cx="0" cy="42" rx="28" ry="4" fill="rgba(0,0,0,0.1)" />
            {[{ y: 30, w: 52, h: 12 }, { y: 20, w: 45, h: 11 }, { y: 11, w: 36, h: 10 }, { y: 3, w: 26, h: 9 }, { y: -5, w: 16, h: 9 }].map((b, i) => (
              <rect key={i} x={-b.w / 2} y={b.y} width={b.w} height={b.h} rx="5" fill={i % 2 ? "#E0982E" : "#F3BE55"} stroke="#B9781C" strokeWidth="1.2" />
            ))}
            <ellipse cx="0" cy="34" rx="5" ry="7" fill="#5B3A08" />
            <text x="0" y="-11" textAnchor="middle" fontSize="14">🐝</text>
          </g>
          <text x={CX} y={dotY(TOTAL_LEVELS) + 130} textAnchor="middle" fontSize="10" fill="#78716C">the hive — home!</text>
          {Array.from({ length: TOTAL_LEVELS }, (_, k) => {
            const lvl = k + 1;
            const x = dotX(lvl);
            const y = dotY(lvl);
            const goal = isGoalLevel(lvl);
            const state = lvl < prog.level ? "done" : lvl === prog.level ? "current" : "locked";
            return (
              <g key={lvl} onClick={state === "current" ? onPlay : undefined} style={{ cursor: state === "current" ? "pointer" : "default" }} data-testid={state === "current" ? "nt-current-dot" : undefined}>
                {goal ? (
                  <>
                    <circle cx={x} cy={y} r={state === "locked" ? 15 : 18} fill={state === "done" ? "#FBBF24" : "#fff"} stroke={state === "current" ? "#16A34A" : "#E7A93A"} strokeWidth={state === "current" ? 3 : 2} />
                    <text x={x} y={y + 7} textAnchor="middle" fontSize="20" opacity={state === "locked" ? 0.45 : 1}>{flowerForLevel(lvl)}</text>
                  </>
                ) : (
                  <circle cx={x} cy={y} r={state === "current" ? 12 : 8} fill={state === "done" ? "#FBBF24" : state === "current" ? "#fff" : "#E7E5E4"} stroke={state === "current" ? "#16A34A" : state === "done" ? "#D97706" : "#D6D3D1"} strokeWidth={state === "current" ? 3 : 1.5} />
                )}
                {state === "done" && !goal && <text x={x} y={y + 4} textAnchor="middle" fontSize="11">🍯</text>}
                {state === "current" && <text x={x} y={y - 20} textAnchor="middle" fontSize="22" className="nt-bee">🐝</text>}
              </g>
            );
          })}
        </svg>
      </div>

      <Button className="w-full" size="lg" onClick={onPlay} data-testid="nt-play">
        {done ? "Replay the last flower" : `Play flower ${prog.level}${isGoalLevel(prog.level) ? " — Goal bloom! 🌟" : ""}`}
      </Button>
    </div>
  );
}

/* ── game view ──────────────────────────────────────────────────────────────── */
function GameView({ game, timeLeft, playable, shakeId, wildActive, prog, onTapCard, onDraw, applyPower, onBack }) {
  const cur = current(game);
  const cardsLeft = game.cards.filter((c) => !c.removed).length;
  const low = timeLeft <= 10;
  return (
    <div className="space-y-3">
      {/* status bar */}
      <div className="flex items-center justify-between rounded-2xl border border-stone-200 bg-white px-4 py-2">
        <Button variant="ghost" size="sm" onClick={onBack} className="text-stone-500"><ArrowLeft className="mr-1 h-4 w-4" /> Map</Button>
        <div className="text-sm font-semibold text-stone-700">🌼 {cardsLeft} left</div>
        <div className={`text-lg font-bold tabular-nums ${low ? "animate-pulse text-red-600" : "text-stone-800"}`} data-testid="nt-timer">⏱️ {timeLeft}s</div>
      </div>
      {wildActive && <div className="rounded-lg bg-emerald-50 px-3 py-1.5 text-center text-sm font-medium text-emerald-700">🌈 Wild bloom active — tap any flower!</div>}

      {/* board */}
      <div className="rounded-2xl border border-stone-200 bg-gradient-to-br from-sky-50 to-emerald-50 p-2">
        <div className="mx-auto" style={{ maxWidth: BOARD_W }}>
          <svg viewBox={`0 0 ${BOARD_W} ${BOARD_H}`} width="100%" style={{ display: "block", overflow: "visible" }} data-testid="nt-board">
            {game.cards.map((card) =>
              card.removed ? null : (
                <BoardCard key={card.id} card={card} isExposed={exposed(game.cards, card.id)} playable={playable.has(card.id)} onTap={onTapCard} shaking={shakeId === card.id} />
              )
            )}
          </svg>
        </div>
      </div>

      {/* stock / current / power-ups */}
      <div className="flex items-end justify-between gap-3 rounded-2xl border border-stone-200 bg-white p-3">
        <button onClick={onDraw} disabled={game.stock.length === 0} className="flex flex-col items-center gap-1 disabled:opacity-40" data-testid="nt-draw">
          <MiniCard back count={game.stock.length} dim={game.stock.length === 0} />
          <span className="text-[11px] text-stone-500">Draw</span>
        </button>
        <div className="flex flex-col items-center gap-1">
          <MiniCard card={cur} bee />
          <span className="text-[11px] text-stone-500">Current bloom</span>
        </div>
        <div className="grid grid-cols-2 gap-1.5">
          {powerTypes.map((t) => (
            <button
              key={t}
              onClick={() => applyPower(t)}
              disabled={prog.pu[t] <= 0 || (t === "wild" && wildActive)}
              title={POWER_META[t].desc}
              className="flex items-center gap-1 rounded-lg border border-stone-200 px-2 py-1 text-xs disabled:opacity-40 hover:border-amber-300"
              data-testid={`nt-power-${t}`}
            >
              <span>{POWER_META[t].icon}</span>
              <b>×{prog.pu[t]}</b>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function HelpModal({ onClose }) {
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4" onClick={onClose}>
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="mb-3 flex items-center justify-between">
          <h2 className="font-display text-xl font-semibold">How to play</h2>
          <button onClick={onClose} className="text-stone-400 hover:text-stone-600"><X className="h-5 w-5" /></button>
        </div>
        <ul className="list-disc space-y-2 pl-5 text-sm text-stone-600">
          <li>Clear the three flower <b>peaks</b> by tapping any <b>uncovered</b> flower whose number is <b>one higher or lower</b> than the current bloom (1 and 13 wrap around).</li>
          <li>Stuck? <b>Draw</b> a new bloom from the pile. Cards behind covered flowers are hidden until uncovered.</li>
          <li>Each flower is <b>timed</b> — clear it before the clock runs out, and every second you save becomes a <b>honey drop</b>.</li>
          <li>The bee can't move on until the current flower is cleared. <b>Retry</b> reshuffles the deal, so you're never truly stuck.</li>
          <li>Every {GOAL_EVERY}th flower is a <b>goal bloom</b> that grants a <b>power-up</b>: {powerTypes.map((t) => POWER_META[t].icon).join(" ")}.</li>
          <li>Travel all {TOTAL_LEVELS} flowers home to the <b>hive</b> and grow it with the honey you carry.</li>
        </ul>
        <Button className="mt-5 w-full" onClick={onClose}>Got it</Button>
      </div>
    </div>
  );
}
